import { useEffect, useState, useRef, type RefObject } from 'react';
import type { Result } from '../types/result';
import type Lead from '../types/leadDelivery';
import fetchDuplicatePrograms from '../utils/data-fetch/fetchDuplicatePrograms';

type UseDuplicateProgramsParams = {
  duplicateResult: Result | null;
  leadRef: RefObject<Lead>;
  degree?: string;
  category?: string;
  subject?: string;
  forceLinkoutSchoolIds?: number[];
  qualifiedIds: string[];
  unqualifiedIds: string[];
};

/**
 * Custom hook to fetch duplicate programs from the same school as the duplicateResult.
 * This hook manages the loading state and cleanup for fetching duplicate programs.
 *
 * @param leadRef - A ref to the lead object. Using a ref prevents re-fetching when lead changes
 *                  but allows accessing the current lead value when needed.
 */
const useDuplicatePrograms = ({
  duplicateResult,
  leadRef,
  degree,
  category,
  subject,
  forceLinkoutSchoolIds,
  qualifiedIds,
  unqualifiedIds,
}: UseDuplicateProgramsParams) => {
  const [duplicatePrograms, setDuplicatePrograms] = useState<Result[]>([]);
  const [duplicateProgramsLoading, setDuplicateProgramsLoading] =
    useState(false);

  useEffect(() => {
    if (!duplicateResult) {
      setDuplicatePrograms([]);
      setDuplicateProgramsLoading(false);
      return;
    }

    const schoolId = parseInt(duplicateResult.schoolId, 10);

    if (Number.isNaN(schoolId)) {
      return;
    }

    let isCancelled = false;
    const presentationUrl =
      typeof window !== 'undefined' ? window.location.href : '';

    const fetchPrograms = async () => {
      setDuplicateProgramsLoading(true);
      try {
        // Ensure leadRef.current exists before making the request
        if (!leadRef.current) {
          console.warn('Lead is not available for fetching duplicate programs');
          if (!isCancelled) {
            setDuplicatePrograms([]);
          }
          return;
        }

        const programs = await fetchDuplicatePrograms({
          lead: leadRef.current,
          degree,
          category,
          subject,
          presentationUrl,
          forceLinkoutSchoolIds,
          schoolId,
          excludeProgramIds: [duplicateResult.programId],
          limit: 50,
          qualified: qualifiedIds,
          unqualified: unqualifiedIds,
        });

        if (!isCancelled) {
          setDuplicatePrograms(programs);
        }
      } catch (error) {
        console.error('Error fetching duplicate programs', error);
        if (!isCancelled) {
          setDuplicatePrograms([]);
        }
      } finally {
        if (!isCancelled) {
          setDuplicateProgramsLoading(false);
        }
      }
    };

    fetchPrograms();

    return () => {
      isCancelled = true;
    };
  }, [
    category,
    degree,
    duplicateResult,
    subject,
    qualifiedIds,
    unqualifiedIds,
    forceLinkoutSchoolIds,
    leadRef,
  ]);

  return {
    duplicatePrograms,
    duplicateProgramsLoading,
  };
};

export default useDuplicatePrograms;
